import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { UserService } from './services/user.service';
import { OpcoService } from './services/opco.service';
import { SentryService } from './config/sentry.service';
import { ServiceWorkerService } from './core/services/service-worker.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DEFAULT_LOCALE } from '@shared/*';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Initializes the data required by the application before rendering the {@link NewAppComponent}.
 * We could move this logic and rename {@link NewAppComponent} instead.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly opcoService = inject(OpcoService);
  private readonly translateService = inject(TranslateService);
  private readonly sentryService = inject(SentryService); // Required to initialize Sentry!
  private readonly serviceWorkerService = inject(ServiceWorkerService); // Required to initialize service worker!

  readonly error: WritableSignal<HttpErrorResponse | null> = signal(null);
  readonly isInitialized = computed(() => this.userService.isInitialized() && this.opcoService.isInitialized());

  ngOnInit(): void {
    // Initialize all data that must be available offline.
    // If a driver is offline, finishing a pickup transport requires raster locations for all depots to be available.
    merge(this.userService.initialize(), this.opcoService.initialize())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            return (this.error.set(error));
          } else {
            throw error; // e.g. login_required error from the Auth0 library which triggers a redirect to the login page.
          }
        }
      });
    this.translateService.setDefaultLang(DEFAULT_LOCALE);
  }
}
